import React, { useState } from 'react';
import { Edit2, Trash2, Plus } from 'lucide-react';
import { TimeSlotCell } from './TimeSlotCell';
import { getCheckpointColor } from './colorUtils';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Sunday', short: 'Sun' },
  { value: 2, label: 'Monday', short: 'Mon' },
  { value: 3, label: 'Tuesday', short: 'Tue' },
  { value: 4, label: 'Wednesday', short: 'Wed' },
  { value: 5, label: 'Thursday', short: 'Thu' },
  { value: 6, label: 'Friday', short: 'Fri' },
  { value: 7, label: 'Saturday', short: 'Sat' }
];

interface CalendarGridProps {
  operatingHours: any[];
  laneTypes: any[];
  checkpoints: any[];
  terminals: any[];
  selectedDays: number[];
  onUpdate: (hourId: number, startTime: string, endTime: string) => Promise<void>;
  onDelete: (hourId: number) => void;
  onAdd: (context: { checkpoint_id: number; checkpoint_name: string; day_of_week: number; lanetype_id: number }) => void;
  canUpdate?: boolean;
}

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  operatingHours,
  laneTypes,
  checkpoints,
  terminals,
  selectedDays,
  onUpdate,
  onDelete,
  onAdd,
  canUpdate = false
}) => {
  // Helper function to get terminal name by ID
  const getTerminalName = (terminalId: number) => {
    const terminal = terminals.find(t => t.terminal_id === terminalId);
    return terminal ? terminal.terminal_name : 'Unknown';
  };
  // Group lane types by checkpoint
  const groupedData = checkpoints.map(checkpoint => ({
    checkpoint,
    laneTypes: laneTypes.filter(lt => lt.checkpoint_id === checkpoint.checkpoint_id)
  })).filter(group => group.laneTypes.length > 0);

  // Get hours for specific lane type and day
  const getHoursForCell = (laneTypeId: number, dayOfWeek: number) => {
    return operatingHours.filter(h => 
      h.lanetype.lanetype_id === laneTypeId && h.day_of_week === dayOfWeek
    );
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  if (groupedData.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-12 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus size={32} className="text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No Operating Hours Yet</h3>
          <p className="text-gray-400 mb-6">
            Use the filters above to select a terminal and checkpoint, then click "+ Add" in any cell to create operating hours.
          </p>
          
          {/* Show button if checkpoint is selected and user has update permission */}
          {canUpdate && checkpoints.length > 0 && (
            <button
              onClick={() => {
                const firstCheckpoint = checkpoints[0];
                onAdd({
                  checkpoint_id: firstCheckpoint.checkpoint_id,
                  checkpoint_name: firstCheckpoint.checkpoint_name,
                  day_of_week: 0,
                  lanetype_id: 0
                });
              }}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors mx-auto"
            >
              <Plus size={20} />
              Add First Operating Hours
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-700">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 sticky left-0 bg-gray-800 z-10 min-w-[140px]">
              Lane Type / Checkpoint
            </th>
            {DAYS_OF_WEEK.filter(day => selectedDays.includes(day.value)).map(day => (
              <th key={day.value} className="px-3 py-2 text-center text-sm font-medium text-gray-300 min-w-[120px]">
                {day.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupedData.map((group, groupIdx) => {
            const checkpointColor = getCheckpointColor(groupIdx);
            
            return (
              <React.Fragment key={group.checkpoint.checkpoint_id}>
                {/* Checkpoint Header Row */}
                <tr className={`border-b border-gray-700 ${checkpointColor.bg}`}>
                  <td colSpan={selectedDays.length + 1} className={`px-4 py-3 border-l-4 ${checkpointColor.border}`}>
                    <div className="font-semibold text-white text-sm">{group.checkpoint.checkpoint_name}</div>
                    <div className="text-xs text-gray-400">Terminal: {getTerminalName(group.checkpoint.terminal_id)}</div>
                  </td>
                </tr>
                
                {/* Lane Type Rows */}
                {group.laneTypes.map((laneType, ltIdx) => (
                  <tr key={laneType.lane_type_id} className={`border-b border-gray-700 hover:bg-gray-750 ${checkpointColor.bg} border-l-4 ${checkpointColor.border}`}>
                    <td className="px-4 py-3 sticky left-0 bg-gray-800 z-10">
                      <div className="text-white font-medium text-sm">{laneType.type_name}</div>
                    </td>
                    {DAYS_OF_WEEK.filter(day => selectedDays.includes(day.value)).map(day => {
                      const hours = getHoursForCell(laneType.lane_type_id, day.value);
                      return (
                        <TimeSlotCell
                          key={`${laneType.lane_type_id}-${day.value}`}
                          hours={hours}
                          laneType={laneType}
                          dayOfWeek={day.value}
                          checkpointId={group.checkpoint.checkpoint_id}
                          checkpointName={group.checkpoint.checkpoint_name}
                          onUpdate={onUpdate}
                          onDelete={onDelete}
                          onAdd={onAdd}
                          canUpdate={canUpdate}
                        />
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};